import { QRCodeSVG } from "qrcode.react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_obsidian-tier/artifacts/za07r3ra_dungeon-eyes-wide-shut-logo-bigger-text.png";

// Corner ornament SVG
const CornerOrnament = () => (
  <svg viewBox="0 0 50 50" className="w-full h-full" fill="#c5a059">
    <path d="M0 0 L15 0 Q5 5 0 15 Z" opacity="0.4" />
    <path d="M0 0 C10 2 20 8 25 25 C8 20 2 10 0 0" opacity="0.2" />
  </svg>
);

export default function MembershipCard({ member, flipped, onFlip }) {
  const getTierClass = (tier) => {
    switch (tier) {
      case 'shadow_initiate': return 'tier-shadow-initiate';
      case 'crimson_devotee': return 'tier-crimson-devotee';
      case 'obsidian_master': return 'tier-obsidian-master';
      default: return 'tier-shadow-initiate';
    }
  };

  return (
    <div 
      className="cursor-pointer"
      onClick={onFlip}
      data-testid="membership-card"
      style={{ perspective: '1500px' }}
    >
      <div 
        className="relative w-[340px] h-[540px] transition-transform duration-700"
        style={{ 
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        {/* Front of Card */}
        <div 
          className="absolute inset-0 rounded-2xl overflow-hidden"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="w-full h-full bg-gradient-to-b from-[#0a0a0a] to-[#050505] border-2 border-[#c5a059]/30 rounded-2xl p-6 flex flex-col relative"
            style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), inset 0 1px 1px rgba(197, 160, 89, 0.1)' }}
          >
            {/* Corner ornaments */}
            <div className="absolute top-3 left-3 w-10 h-10 opacity-40"><CornerOrnament /></div>
            <div className="absolute top-3 right-3 w-10 h-10 opacity-40" style={{ transform: 'scaleX(-1)' }}><CornerOrnament /></div>
            <div className="absolute bottom-3 left-3 w-10 h-10 opacity-40" style={{ transform: 'scaleY(-1)' }}><CornerOrnament /></div>
            <div className="absolute bottom-3 right-3 w-10 h-10 opacity-40" style={{ transform: 'scale(-1)' }}><CornerOrnament /></div>

            {/* Header */}
            <div className="text-center mb-4">
              <p className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-[#c5a059]/50">
                Dungeon-Eyes Wide Shut
              </p>
              <h2 className="font-cinzel text-xs tracking-[0.2em] uppercase mt-1" style={{ color: '#c5a059' }}>
                Loyalty Shadow Membership
              </h2>
            </div>

            {/* Logo */}
            <div className="flex-1 flex items-center justify-center">
              <img 
                src={LOGO_URL}
                alt="Dungeon Eyes Wide Shut"
                className="w-48 h-auto"
                style={{ filter: 'drop-shadow(0 0 10px rgba(197, 160, 89, 0.3))' }}
              />
            </div>

            {/* Tier Badge */}
            <div className="text-center mb-4">
              <span className={`tier-badge ${getTierClass(member.tier)}`}>
                {member.tier_name}
              </span>
            </div>

            {/* Member Info */}
            <div className="text-center mb-4">
              <p className="font-cinzel text-lg tracking-wider gold-gradient-text">
                {member.display_name}
              </p>
              <p className="text-[#c5a059]/50 text-xs tracking-[0.15em] mt-1">
                {member.member_id}
              </p>
            </div>

            {/* Progress Masks */}
            <div className="mb-4">
              <div className="flex justify-center gap-3">
                {[...Array(5)].map((_, i) => (
                  <div 
                    key={i}
                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                      i < member.current_cycle_visits 
                        ? 'border-[#c5a059] bg-[#c5a059]/20' 
                        : 'border-[#c5a059]/20 bg-transparent'
                    }`}
                    style={{ 
                      boxShadow: i < member.current_cycle_visits ? '0 0 10px rgba(197, 160, 89, 0.5)' : 'none' 
                    }}
                  >
                    {i < member.current_cycle_visits && (
                      <div className="w-3 h-3 rounded-full bg-[#c5a059]" />
                    )}
                  </div>
                ))}
              </div>
              <p className="text-center text-[#c5a059]/40 text-[10px] tracking-wider mt-2">
                {member.visits_to_next_reward} visit{member.visits_to_next_reward !== 1 ? 's' : ''} to free evening
              </p>
            </div>

            {/* Footer */}
            <div className="text-center">
              <p className="text-[#c5a059]/30 text-[9px] tracking-[0.1em]">
                Member since {new Date(member.member_since).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
              </p>
              <p className="text-[#c5a059]/20 text-[8px] tracking-[0.15em] mt-1">
                dungeon-eyeswideshut.co.uk
              </p>
            </div>
          </div>
        </div>

        {/* Back of Card (QR Code) */}
        <div 
          className="absolute inset-0 rounded-2xl overflow-hidden"
          style={{ 
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          <div className="w-full h-full bg-gradient-to-b from-[#0a0a0a] to-[#050505] border-2 border-[#c5a059]/30 rounded-2xl p-6 flex flex-col items-center justify-center relative"
            style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), inset 0 1px 1px rgba(197, 160, 89, 0.1)' }}
          >
            {/* Corner ornaments */}
            <div className="absolute top-3 left-3 w-10 h-10 opacity-40"><CornerOrnament /></div>
            <div className="absolute top-3 right-3 w-10 h-10 opacity-40" style={{ transform: 'scaleX(-1)' }}><CornerOrnament /></div>
            <div className="absolute bottom-3 left-3 w-10 h-10 opacity-40" style={{ transform: 'scaleY(-1)' }}><CornerOrnament /></div>
            <div className="absolute bottom-3 right-3 w-10 h-10 opacity-40" style={{ transform: 'scale(-1)' }}><CornerOrnament /></div>

            <p className="font-cinzel text-sm tracking-[0.2em] uppercase mb-6" style={{ color: '#c5a059' }}>
              Member Profile
            </p>

            {/* QR Code */}
            <div className="bg-white p-4 rounded-xl mb-6" style={{ boxShadow: '0 0 30px rgba(197, 160, 89, 0.2)' }}>
              <QRCodeSVG 
                value={`https://dungeon-eyeswideshut.co.uk/member/${member.member_id}`}
                size={160}
                bgColor="#ffffff"
                fgColor="#050505"
                level="H"
                includeMargin={false}
              />
            </div>

            <p className="font-cinzel text-lg tracking-wider text-[#e5c07b] mb-1">
              {member.display_name}
            </p>
            <p className="text-[#c5a059]/50 text-xs tracking-wider mb-6">
              {member.member_id}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-8 w-full max-w-[200px]">
              <div className="text-center">
                <p className="font-cinzel text-3xl text-[#e5c07b]">{member.total_visits}</p>
                <p className="text-[#c5a059]/40 text-[10px] tracking-wider">VISITS</p>
              </div>
              <div className="text-center">
                <p className="font-cinzel text-3xl" style={{ color: member.free_evenings_earned - member.free_evenings_redeemed > 0 ? '#b30000' : '#e5c07b' }}>
                  {member.free_evenings_earned - member.free_evenings_redeemed}
                </p>
                <p className="text-[#c5a059]/40 text-[10px] tracking-wider">REWARDS</p>
              </div>
            </div>

            <p className="text-[#c5a059]/30 text-[9px] tracking-[0.15em] mt-8">
              Tap to return
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
